import { isTerminalOutcome } from '@/components/dashboard/dashboardState'
import { RejectionReason } from '@/components/onboarding/RejectionReason'
import { useUpdateProfile } from '@/hooks/mutations/onboarding/useUpdateProfile'
import { useDriverOnboarding } from '@/hooks/queries/onboarding/useDriverOnboarding'
import { useDriverProfile } from '@/hooks/queries/onboarding/useDriverProfile'
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { LoaderCircleIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_auth/carriers/$slug/driver-onboarding/profile')({
  component: ProfilePage,
  staticData: {
    title: 'route.profile',
  },
})

function ProfilePage() {
  const { t } = useTranslation('ui')
  const { data, isLoading } = useDriverProfile()
  const { data: onboarding, isLoading: isOnboardingLoading } = useDriverOnboarding({ poll: false })
  const updateProfile = useUpdateProfile()
  const [form, setForm] = useState<Record<string, string>>({})
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    if (data?.driver) {
      const d = data.driver
      setForm({
        firstName: d.firstName ?? '',
        lastName: d.lastName ?? '',
        phone: d.phone ?? '',
        dateOfBirth: d.dateOfBirth ?? '',
      })
      setIsDirty(false)
    }
  }, [data])

  if (isLoading || isOnboardingLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoaderCircleIcon className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!data) return null

  // Nothing links here, so a bookmark is the way in: the page tests the outcome
  // itself rather than letting a decided applicant fill a form the server will
  // refuse on Save (ONB-26).
  const application = onboarding?.application
  const isLocked = isTerminalOutcome(application?.phase, data.driver?.status)

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setIsDirty(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Only send non-empty changed fields
    const payload: Record<string, string> = {}
    for (const [key, value] of Object.entries(form)) {
      if (value) payload[key] = value
    }
    updateProfile.mutate(payload)
    setIsDirty(false)
  }

  const handleCancel = () => {
    if (data?.driver) {
      const d = data.driver
      setForm({
        firstName: d.firstName ?? '',
        lastName: d.lastName ?? '',
        phone: d.phone ?? '',
        dateOfBirth: d.dateOfBirth ?? '',
      })
      setIsDirty(false)
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t('profile.title')}</h1>

      {isLocked && (
        <div role="status" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm">
          <p className="text-gray-700">{t('profile.locked')}</p>
          <RejectionReason reason={application?.rejectionReason} className="mt-3" />
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <fieldset disabled={isLocked} className="min-w-0">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('profile.section.personal')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>{t('profile.firstName')}</Label>
                  <Input
                    value={form.firstName ?? ''}
                    onChange={(e) => updateField('firstName', e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>{t('profile.lastName')}</Label>
                  <Input
                    value={form.lastName ?? ''}
                    onChange={(e) => updateField('lastName', e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>{t('profile.phone')}</Label>
                <Input
                  value={form.phone ?? ''}
                  onChange={(e) => updateField('phone', e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label>{t('profile.dateOfBirth')}</Label>
                <Input
                  type="date"
                  value={form.dateOfBirth ?? ''}
                  onChange={(e) => updateField('dateOfBirth', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </fieldset>

        {!isLocked && (
          <div className="flex gap-3">
            <Button type="submit" isDisabled={!isDirty || updateProfile.isPending}>
              {updateProfile.isPending && <LoaderCircleIcon className="animate-spin" />}
              {updateProfile.isPending ? t('profile.saving') : t('profile.save')}
            </Button>
            {isDirty && (
              <Button type="button" variant="outline" onPress={handleCancel}>
                {t('profile.cancel')}
              </Button>
            )}
          </div>
        )}
      </form>
    </div>
  )
}
